import React, { useMemo } from 'react'
import {
    Box,
    Button,
    Typography,
    makeStyles,
    FormControlLabel,
    Checkbox,
    useTheme,
} from '@material-ui/core'
import { SearchField } from '../../../../components/SearchField'
import { Selector } from '../../../../components/Selector'
import { Organization, CompanyStatus } from '../../../../models'
import { statesList } from '../../../../helpers'
import {
    EDITABLE_STATUS_OPTIONS,
    formatStatusLabel,
    getStatusDotColor,
} from '../utils/statusUtils'

const STATUS_LEGEND = [
    { status: CompanyStatus.ACTIVE, label: 'Active' },
    { status: CompanyStatus.IN_ACTIVE, label: 'Inactive' },
    { status: CompanyStatus.TEST, label: 'Test' },
] as const

interface SearchState {
    text: string
    userEmail: string
}

const STATE_SELECTOR_DATA = statesList.map((code, index) => ({
    id: index,
    name: code,
}))

type StatusSelectorItem = {
    id: number
    name: string
    status?: CompanyStatus
}

const STATUS_SELECTOR_DATA: StatusSelectorItem[] = EDITABLE_STATUS_OPTIONS.map(
    (statusOption, index) => ({
        id: index,
        name: formatStatusLabel(statusOption),
        status: statusOption,
    }),
)

const StatusOptionLabel = ({
    status,
    label,
    dotClassName,
}: {
    status?: CompanyStatus
    label: string
    dotClassName: string
}) => {
    const theme = useTheme()

    return (
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: theme.spacing(1),
                lineHeight: 1,
            }}
        >
            {status && (
                <span
                    className={dotClassName}
                    style={{
                        backgroundColor: getStatusDotColor(status, theme),
                        flexShrink: 0,
                    }}
                />
            )}
            <span style={{ lineHeight: 1 }}>{label}</span>
        </span>
    )
}

interface SearchHeaderProps {
    searchState: SearchState
    onSearchChange: (
        field: keyof SearchState,
    ) => (e: React.ChangeEvent<HTMLInputElement>) => void
    showOnlyDisconnected: boolean
    onShowOnlyDisconnectedChange: (value: boolean) => void
    hasBillingSetup: boolean
    onHasBillingSetupChange: (value: boolean) => void
    status: CompanyStatus | 'ALL'
    onStatusChange: (status: CompanyStatus | 'ALL') => void
    organizationId: number | null
    onOrganizationChange: (organizationId: number | null) => void
    organizations: Organization[]
    organizationsLoading: boolean
    stateFilter: string | null
    onStateChange: (state: string | null) => void
    bulkMode: boolean
    onToggleBulkMode: () => void
    bulkSelectedCount: number
    matchingFilterCount: number
    onSelectAllMatching: () => void
    isSelectingAllMatching: boolean
    onBulkAdd: () => void
    onBulkEdit: () => void
    onBulkUpdateStatus: (anchorEl: HTMLElement) => void
    onDeselectAll: () => void
    onClickCopyConfigs: () => void
}

const useStyles = makeStyles((theme) => ({
    header: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        gap: theme.spacing(1),
    },
    searchContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(2),
        flex: 1,
        alignItems: 'center',
        width: '100%',
    },
    filtersGroup: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(2),
        alignItems: 'center',
        flex: 1,
        minWidth: 0,
    },
    actionsGroup: {
        display: 'flex',
        gap: theme.spacing(2),
        alignItems: 'center',
        marginLeft: 'auto',
        flexShrink: 0,
    },
    searchField: {
        minWidth: 180,
    },
    bulkRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
    },
    primaryActionButton: {
        maxHeight: 40,
        backgroundColor: theme.palette.primary.dark,
        color: theme.palette.common.white,
        '&:hover': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
        },
    },
    statusLegend: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        alignSelf: 'flex-start',
        gap: theme.spacing(0.25),
        flexShrink: 0,
        paddingTop: theme.spacing(0.5),
    },
    statusLegendItem: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
    },
    statusLegendDot: {
        width: 8,
        height: 8,
        borderRadius: '50%',
        flexShrink: 0,
    },
}))

export const SearchHeader: React.FC<SearchHeaderProps> = ({
    searchState,
    onSearchChange,
    showOnlyDisconnected,
    onShowOnlyDisconnectedChange,
    hasBillingSetup,
    onHasBillingSetupChange,
    status,
    onStatusChange,
    organizationId,
    onOrganizationChange,
    organizations,
    organizationsLoading,
    stateFilter,
    onStateChange,
    bulkMode,
    onToggleBulkMode,
    bulkSelectedCount,
    matchingFilterCount,
    onSelectAllMatching,
    isSelectingAllMatching,
    onBulkAdd,
    onBulkEdit,
    onBulkUpdateStatus,
    onDeselectAll,
    onClickCopyConfigs,
}) => {
    const classes = useStyles()
    const theme = useTheme()

    const stateSelectorValue = useMemo(() => {
        if (!stateFilter) {
            return -1
        }
        const index = statesList.indexOf(stateFilter)
        return index === -1 ? -1 : index
    }, [stateFilter])

    const statusSelectorValue = useMemo(() => {
        if (status === 'ALL') {
            return -1
        }
        const index = EDITABLE_STATUS_OPTIONS.indexOf(status)
        return index === -1 ? -1 : index
    }, [status])

    return (
        <Box className={classes.header}>
            <div className={classes.searchContainer}>
                <div className={classes.statusLegend}>
                    {STATUS_LEGEND.map(({ status, label }) => (
                        <span key={status} className={classes.statusLegendItem}>
                            <span
                                className={classes.statusLegendDot}
                                style={{
                                    backgroundColor: getStatusDotColor(
                                        status,
                                        theme,
                                    ),
                                }}
                            />
                            <Typography
                                variant="caption"
                                color="textSecondary"
                                style={{ fontWeight: 600 }}
                            >
                                {label}
                            </Typography>
                        </span>
                    ))}
                </div>
                <div className={classes.filtersGroup}>
                    <SearchField
                        className={classes.searchField}
                        placeholder="Search properties..."
                        value={searchState.text}
                        onChange={onSearchChange('text')}
                    />
                    <SearchField
                        className={classes.searchField}
                        placeholder="Filter by user email..."
                        value={searchState.userEmail}
                        onChange={onSearchChange('userEmail')}
                    />
                    <Selector
                        label="Organization"
                        currentValue={organizationId ?? -1}
                        onChange={(e) => {
                            const nextId = Number(e.target.value)
                            onOrganizationChange(nextId === -1 ? null : nextId)
                        }}
                        data={organizations}
                        getDisplayString={(org) => org.name}
                        searchable
                        noSelectionLabel="All organizations"
                        size="small"
                        variant="outlined"
                        customStyle={{
                            formControl: { minWidth: 220 },
                        }}
                        disabled={organizationsLoading}
                        maxItems={25}
                        popupHeight={420}
                    />
                    <Selector
                        label="State"
                        currentValue={stateSelectorValue}
                        onChange={(e) => {
                            const nextId = Number(e.target.value)
                            onStateChange(
                                nextId === -1 ? null : statesList[nextId],
                            )
                        }}
                        data={STATE_SELECTOR_DATA}
                        getDisplayString={(item) => item.name}
                        noSelectionLabel="All states"
                        size="small"
                        variant="outlined"
                        customStyle={{
                            formControl: { minWidth: 120 },
                        }}
                    />
                    <Selector
                        label="Status"
                        currentValue={statusSelectorValue}
                        onChange={(e) => {
                            const nextId = Number(e.target.value)
                            onStatusChange(
                                nextId === -1
                                    ? 'ALL'
                                    : STATUS_SELECTOR_DATA[nextId].status!,
                            )
                        }}
                        data={STATUS_SELECTOR_DATA}
                        getDisplayString={(item) => item.name}
                        getOrnament={(item) =>
                            item.status ? (
                                <span
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        alignSelf: 'center',
                                        marginRight: theme.spacing(1),
                                    }}
                                >
                                    <span
                                        className={classes.statusLegendDot}
                                        style={{
                                            backgroundColor: getStatusDotColor(
                                                item.status,
                                                theme,
                                            ),
                                        }}
                                    />
                                </span>
                            ) : (
                                <></>
                            )
                        }
                        customRenderValue={() => (
                            <StatusOptionLabel
                                status={status === 'ALL' ? undefined : status}
                                label={
                                    status === 'ALL'
                                        ? 'All statuses'
                                        : formatStatusLabel(status)
                                }
                                dotClassName={classes.statusLegendDot}
                            />
                        )}
                        noSelectionLabel="All statuses"
                        size="small"
                        variant="outlined"
                        customStyle={{
                            formControl: { minWidth: 160 },
                            menuItem: { alignItems: 'center' },
                            select: { display: 'flex', alignItems: 'center' },
                            text: { lineHeight: 1 },
                        }}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={showOnlyDisconnected}
                                onChange={(e) =>
                                    onShowOnlyDisconnectedChange(
                                        e.target.checked,
                                    )
                                }
                                color="primary"
                                size="small"
                            />
                        }
                        label="Integration errors only"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={hasBillingSetup}
                                onChange={(e) =>
                                    onHasBillingSetupChange(e.target.checked)
                                }
                                color="primary"
                                size="small"
                            />
                        }
                        label="Has billing setup"
                    />
                </div>
                <div className={classes.actionsGroup}>
                    <Button
                        variant="contained"
                        className={classes.primaryActionButton}
                        onClick={onToggleBulkMode}
                        size="small"
                    >
                        {bulkMode ? 'Exit bulk mode' : 'Bulk actions'}
                    </Button>
                    <Button
                        variant="contained"
                        className={classes.primaryActionButton}
                        onClick={onClickCopyConfigs}
                        size="small"
                    >
                        Copy Configs
                    </Button>
                </div>
            </div>

            {bulkMode && (
                <div className={classes.bulkRow}>
                    <Typography variant="body2" color="textSecondary">
                        {bulkSelectedCount} propert
                        {bulkSelectedCount === 1 ? 'y' : 'ies'} selected
                        {matchingFilterCount > 0 &&
                            ` (${matchingFilterCount} match current filters)`}
                    </Typography>
                    {matchingFilterCount > 0 && (
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={onSelectAllMatching}
                            size="small"
                            disabled={isSelectingAllMatching}
                        >
                            {isSelectingAllMatching
                                ? 'Selecting…'
                                : `Select all ${matchingFilterCount} matching`}
                        </Button>
                    )}
                    {bulkSelectedCount > 0 && (
                        <>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={onBulkAdd}
                                size="small"
                            >
                                Bulk Add User
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={onBulkEdit}
                                size="small"
                            >
                                Bulk Edit Billing
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={(e) =>
                                    onBulkUpdateStatus(e.currentTarget)
                                }
                                size="small"
                            >
                                Bulk Update Status
                            </Button>
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={onDeselectAll}
                                size="small"
                            >
                                Deselect All
                            </Button>
                        </>
                    )}
                </div>
            )}
        </Box>
    )
}
